export const DASH = "—";

const isNum = (v) => typeof v === "number" && Number.isFinite(v);

export const num = (v, digits = 0) =>
  isNum(v)
    ? v.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })
    : DASH;

export const percentColor = (percent) => {
  if (!isNum(percent)) return "#d9d9d9";
  if (percent > 85) return "#ff4d4f";
  if (percent >= 70) return "#faad14";
  return "#52c41a";
};

export const loadPercent = (load, cores) =>
  isNum(load) && isNum(cores) && cores > 0 ? (load / cores) * 100 : null;

export const usageLine = (used, total, percent, unit) =>
  isNum(used) && isNum(total)
    ? `${num(used)} ${unit} of ${num(total)} ${unit}${isNum(percent) ? ` (${num(percent, 1)}%)` : ""}`
    : DASH;

export const diskLine = (disk) =>
  disk && isNum(disk.used_gb) && isNum(disk.total_gb)
    ? `${num(disk.used_gb, 1)} GB of ${num(disk.total_gb, 1)} GB used (${num(disk.used_percent, 1)}%), ${num(disk.free_gb, 1)} GB free`
    : DASH;

export const loadLine = (load, cores) =>
  Array.isArray(load) && isNum(load[0])
    ? `${num(load[0], 2)} / ${isNum(cores) ? cores : DASH} cores`
    : DASH;

export const loadAverages = (load) =>
  Array.isArray(load) && load.length === 3 && load.every(isNum)
    ? `5 min ${num(load[1], 2)} · 15 min ${num(load[2], 2)}`
    : DASH;

export const uptimeText = (seconds) => {
  if (!isNum(seconds)) return DASH;
  const minutes = Math.floor(seconds / 60);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  if (days > 0) return `${days} ${days === 1 ? "day" : "days"} ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes % 60} min`;
  return `${minutes} min`;
};

export const ageText = (iso, nowMs) => {
  const then = Date.parse(iso);
  if (!iso || Number.isNaN(then)) return DASH;
  const minutes = Math.max(0, Math.floor((nowMs - then) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
};

export const durationText = (iso, nowMs) => {
  const then = Date.parse(iso);
  if (!iso || Number.isNaN(then)) return DASH;
  return uptimeText(Math.max(0, (nowMs - then) / 1000));
};

export const clockTime = (iso) => {
  const d = new Date(iso);
  return iso && !Number.isNaN(d.getTime())
    ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" })
    : DASH;
};

export const clockMinutes = (iso) => {
  const d = new Date(iso);
  return iso && !Number.isNaN(d.getTime())
    ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    : DASH;
};

export const mediaLine = (media) =>
  media && isNum(media.size_mb)
    ? `${num(media.size_mb)} MB${isNum(media.files) ? ` in ${num(media.files)} files` : ""}${media.complete === false ? " (partial count)" : ""}`
    : DASH;

export const processesLine = (processes) => {
  const entries = processes ? Object.entries(processes).filter(([, n]) => isNum(n)) : [];
  return entries.length ? entries.map(([name, n]) => `${name} ${n}`).join(" · ") : DASH;
};

export const runsText = (minutes) => {
  if (!isNum(minutes)) return DASH;
  if (minutes === 1) return "every minute";
  if (minutes === 1440) return "daily";
  if (minutes % 60 === 0) return `every ${minutes / 60} h`;
  return `every ${minutes} min`;
};

export const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : DASH);

export const jobStatus = (job) => {
  if (job.installed === null || job.installed === undefined) return { label: "Unknown", color: "default" };
  if (job.installed === false) return { label: "Not installed", color: "default" };
  if (job.python_found === false) return { label: "Broken", color: "red" };
  if (job.late === true) return { label: "Late", color: "red" };
  if (job.late === false) return { label: "OK", color: "green" };
  return { label: "Unknown", color: "default" };
};
